import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import {
  totalCash,
  totalCashOutflow,
  totalOutflow,
  totalSpend,
  spendEntriesOf,
} from '@domain/finance'
import type { Account, Category, CreditCard, Transaction } from '@domain/finance'
import { addMonths } from '@shared/date/reference-month'
import type { ReferenceMonth } from '@shared/date/reference-month'
import { absCents } from '@shared/money/cents'
import { formatBRL } from '@shared/money/format'
import { frasePendencias, resumirNotas } from '@domain/receipt'
import { IconeNota, IconeRelatorio, IconeSino } from '@ui/shell/icones'
import { BotaoDeIcone } from '@ui/shell/BotaoDeIcone'
import { currentReferenceMonth, todayInHousehold } from '@shared/date/timezone'
import { Avatar } from '@ui/avatar/Avatar'
import { useReadyUser } from '@ui/auth/auth-context'
import { Glass } from '@ui/glass/Glass'
import { Numero as NumeroDeDisplay } from '@ui/glass/Numero'
import { LinhaDeTransacao } from '@ui/finance/LinhaDeTransacao'
import { Saldo } from '@ui/finance/Saldo'
import { GastosPorCategoria } from '@ui/finance/GastosPorCategoria'
import { groupByDay, rotuloDasFuturas, separarFuturos } from '@ui/finance/day-groups'
import type { DayGroup } from '@ui/finance/day-groups'
import { Dobra } from '@ui/shell/Dobra'
import { monthLabel } from '@ui/finance/labels'
import { useFinanceRepositories } from '@ui/finance/finance-context'
import { useMonthTransactions, useMonthlyBook } from '@ui/finance/use-monthly-book'
import type { PreviousMonth } from '@ui/finance/use-monthly-book'
import { variationLabel, variationOf } from '@ui/finance/variation'
import { dicaDoAbatimento, vinculosDoMes } from '@ui/finance/abatimento/vinculos'

/**
 * A primeira tela. *"Responde 'como estamos?' sem que ninguém precise tocar em
 * nada"* — `Docs/04-Design/Telas/Dashboard.md`.
 *
 * ## O mês é sempre o CORRENTE, e não há seletor
 *
 * Quem escolhe mês é [[Transações]]. Um seletor aqui daria duas telas fazendo a
 * mesma coisa, e a pergunta desta é sobre agora.
 *
 * ## Os dois números têm os rótulos que a decisão 141 fixou
 *
 * A nota carregava um aviso — *"os dois substantivos estão imprecisos, e a
 * decisão é desta fase"* — porque `totalSpend` e `totalCash` são **líquidos**:
 * nenhum dos dois exclui receita, e um mês com salário dá número positivo nos
 * dois. A decisão foi separar: o número grande é o **gasto** (só as saídas), e o
 * líquido continua visível ao lado, menor. É o mesmo bloco de Transações, e de
 * propósito — duas telas que respondem a mesma pergunta com números diferentes
 * seria pior que qualquer rótulo impreciso.
 *
 * ## A variação não bloqueia
 *
 * O mês anterior vem por um hook próprio e mais barato (`useMonthTransactions`),
 * e a seta aparece quando ele chega. Um número grande que espera o mês passado
 * para aparecer demora pela razão errada.
 */

/** Cinco, como a nota pede. */
const ULTIMAS = 5

/**
 * O sino, com a contagem de não lidas.
 *
 * **Ele não bloqueia nada, e a contagem chega depois.** Uma leitura a mais no
 * cabeçalho não pode segurar o saldo nem os dois números; enquanto ela não
 * volta, o sino aparece sem badge. É a mesma escolha da variação mês a mês.
 *
 * **Falha em silêncio, e é o único lugar do app onde isso é certo.** Um `alert`
 * dizendo "não foi possível contar as notificações" ocuparia a tela com um
 * problema que não impede nada — a pessoa abre a central e vê a lista, ou vê o
 * erro lá, onde ele importa.
 */
/**
 * Um atalho grande da home: ícone, nome e uma linha do que ele faz.
 *
 * ## `flat`, e o motivo é uma conta
 *
 * O orçamento do `Glass` é **6 `frost` por viewport**, e passar dele derrubou a
 * composição do Chromium num protótipo — página em branco, não "mais lento"
 * (decisão 202). Esta tela já estava em **5**, contados no navegador; dois
 * cartões `frost` a levariam a 7, que é exatamente onde a varredura de botões
 * estourou na decisão 233.
 *
 * `flat` mantém a borda, o rim specular e a sombra — o que custa é o desfoque,
 * e num cartão de 80px ele quase não aparece.
 */
function Cartao({
  para,
  nome,
  dica,
  children,
}: {
  para: string
  nome: string
  dica: string
  children: ReactNode
}) {
  return (
    <Glass level="flat" as={Link} to={para} className="flex flex-col gap-1 p-4 text-[var(--text)]">
      <span className="text-[var(--brand)]">{children}</span>
      <span className="text-sm font-medium">{nome}</span>
      <span className="text-xs text-[var(--text-muted)]">{dica}</span>
    </Glass>
  )
}

/**
 * O cartão de Notas, com o que ainda falta fazer nelas.
 *
 * ## Ele LÊ, ao contrário do de Relatórios
 *
 * Uma leitura a mais por abertura da home, e ela se paga: "3 a revisar" é o
 * chamado à ação que a nota de desenho pede do Dashboard, e "Fotografe o cupom"
 * era uma frase fixa que não sabia se havia algo esperando. A conta de qual
 * frase mostrar mora no domínio (`frasePendencias`), com casos próprios.
 *
 * ## Falha de leitura vira a frase neutra, e não um erro
 *
 * O cartão é um atalho: se a contagem não vier, ele ainda leva às Notas. Trocar
 * um convite por "não foi possível" quebraria o atalho por causa do enfeite.
 */
function CartaoDeNotas() {
  const { receiptRepository } = useFinanceRepositories()
  const [frase, setFrase] = useState('Fotografe o cupom')

  useEffect(() => {
    let live = true
    void receiptRepository
      .list()
      .then((notas) => {
        if (live) setFrase(frasePendencias(resumirNotas(notas)))
      })
      .catch((error: unknown) => {
        console.error('CartaoDeNotas: a contagem falhou', error)
      })
    return () => {
      live = false
    }
  }, [receiptRepository])

  return (
    <Cartao para="/notas" nome="Notas" dica={frase}>
      <IconeNota />
    </Cartao>
  )
}

function Sino() {
  const { notificationRepository } = useFinanceRepositories()
  const [naoLidas, setNaoLidas] = useState<number | null>(null)

  useEffect(() => {
    let live = true
    void notificationRepository
      .listMine()
      .then((lista) => {
        if (live) setNaoLidas(lista.filter((n) => n.readAt === null).length)
      })
      .catch((error: unknown) => {
        console.error('Sino: a contagem de não lidas falhou', error)
      })
    return () => {
      live = false
    }
  }, [notificationRepository])

  const pendentes = naoLidas !== null && naoLidas > 0

  return (
    <BotaoDeIcone
      as={Link}
      to="/notificacoes"
      rotulo={pendentes ? `Notificações, ${String(naoLidas)} não lidas` : 'Notificações'}
    >
      <IconeSino />
      {/* A contagem é uma MARCA, e não um número ao lado do ícone: o rótulo
          acessível já diz quantas são, e no cabeçalho o que importa de relance
          é "tem algo" — o número exato se lê ao abrir. Com mais de nove ela
          vira "9+" para não empurrar a bolinha para fora do botão. */}
      {pendentes && (
        <span
          aria-hidden="true"
          className="absolute -top-0.5 -right-0.5 grid h-4 min-w-4 place-items-center rounded-full px-1 text-[10px] font-semibold text-[var(--bg)]"
          style={{ background: 'var(--brand)' }}
        >
          {naoLidas > 9 ? '9+' : naoLidas}
        </span>
      )}
    </BotaoDeIcone>
  )
}

function Numero({
  id,
  title,
  outflow,
  net,
  previousOutflow,
}: {
  id: string
  title: string
  outflow: number
  net: number
  /** `null` enquanto o mês anterior não chegou — a seta simplesmente não aparece. */
  previousOutflow: number | null
}) {
  const variacao = previousOutflow === null ? null : variationOf(outflow, previousOutflow)

  return (
    <div className="flex-1">
      {/* Os rótulos "(competência)" e "(caixa)" aparecem SEMPRE, sem exceção. São
          conceitos que se aprende uma vez, e o app é quem ensina. */}
      <h3 id={id} className="text-xs text-[var(--text-muted)]">
        {title}
      </h3>
      {/*
        Fraunces, e não a face de interface: é o número que a pessoa abriu o app
        para ver. `NumeroDeDisplay` carrega a plaquinha só quando pedida — aqui
        o cartão é `frost` sob texto, então ela entra.
      */}
      <p aria-labelledby={id} className="mt-0.5">
        <NumeroDeDisplay tamanho="medio">{formatBRL(absCents(outflow))}</NumeroDeDisplay>
      </p>
      <p className="mt-1 text-xs text-[var(--text-muted)]">
        {/* A cor não entra: `up` num gasto é ruim e num líquido depende, e
            pintar de vermelho o que a função não sabe julgar seria a tela
            afirmando mais do que sabe. Ver `variation.ts`. */}
        {variacao === null ? 'sem mês anterior para comparar' : variationLabel(variacao)}
        {' · líquido '}
        <span className="tabular-nums">{formatBRL(net)}</span>
      </p>
    </div>
  )
}

/**
 * As últimas cinco, com data relativa. **Da união das duas visões**, pelo mesmo
 * motivo da lista de Transações (decisão 142b): só a competência esconderia o
 * pagamento da fatura, que é a transação que mais mexe com o dinheiro do mês.
 */
/**
 * As últimas transações do mês, na home.
 *
 * ## A MESMA linha da aba de Transações, e não uma parecida
 *
 * Ela era uma segunda implementação: `flex flex-wrap` com descrição, categoria,
 * valor e data do mesmo peso — o defeito que a decisão 263 mediu e consertou
 * lá. Numa transação de nome longo ("Pagamento efetuado|LAKE BOULEVARD DA SERRA
 * SPE LTDA") a descrição quebrava em quatro linhas e empurrava o valor para
 * baixo; a coluna de valores simplesmente não existia.
 *
 * Agora é `LinhaDeTransacao`, a mesma das duas telas — com o ponto de sem
 * categoria, o ícone do instrumento, o valor alinhado, o toque que navega e o
 * segurar que abre a categoria. Corrigir uma coisa passa a corrigir as duas.
 *
 * ## Os cabeçalhos de dia vêm junto, e a coluna de data foi embora
 *
 * Aqui a data morava numa coluna estreita à direita ("Hoje", "28 de setembro"),
 * e lá em cabeçalhos de grupo. Manter as duas formas seria manter as duas
 * implementações pela porta dos fundos — e a coluna era o que espremia a
 * descrição.
 */
function Ultimas({
  competence,
  cash,
  categories,
  creditCards,
  accounts,
  today,
  onCategorize,
  onCategoriasMudaram,
  onConfirmar,
}: {
  competence: Transaction[]
  cash: Transaction[]
  categories: Category[]
  creditCards: CreditCard[]
  accounts: Account[]
  today: string
  onCategorize: (id: string, categoryId: string | null) => Promise<void>
  onCategoriasMudaram: () => void
  onConfirmar: (id: string) => Promise<void>
}) {
  // Uma aberta por vez, como na aba — ver `LinhaDeTransacao`.
  const [abertaId, setAbertaId] = useState<string | null>(null)

  const porId = new Map(competence.map((t) => [t.id, t]))
  for (const t of cash) porId.set(t.id, t)
  const transfers = new Set(
    categories.filter((c) => c.kind === 'transfer').map((c) => c.id),
  )
  const vinculos = vinculosDoMes([...porId.values()])

  // O que ainda não aconteceu não é "recente": fica numa dobra fechada, e as
  // cinco vagas são do que já passou. Ver `separarFuturos`.
  const { futuros, resto } = separarFuturos(groupByDay([...porId.values()], today), today)

  // As CINCO mais recentes, e só depois agrupadas: agrupar antes daria cinco
  // por dia em vez de cinco no total.
  const recentes = resto.flatMap((g) => g.transactions).slice(0, ULTIMAS)
  const grupos = groupByDay(recentes, today)

  if (recentes.length === 0) {
    return <p className="text-sm text-[var(--text-muted)]">Nada ainda neste mês.</p>
  }

  const diaDosRecentes = (grupo: DayGroup) => (
    <div key={grupo.date} className="mb-2">
      <h3 className="mb-1 text-xs font-semibold tracking-tight text-[var(--text-faint)]">
        {grupo.relative === null ? grupo.full : `${grupo.relative} · ${grupo.full}`}
      </h3>
      <ul className="text-sm">
        {grupo.transactions.map((transaction) => (
          <LinhaDeTransacao
            key={transaction.id}
            transaction={transaction}
            categories={categories}
            creditCards={creditCards}
            accounts={accounts}
            transfers={transfers}
            aberta={abertaId === transaction.id}
            onAbrir={() => {
              setAbertaId((atual) => (atual === transaction.id ? null : transaction.id))
            }}
            onCategorize={onCategorize}
            onCategoriasMudaram={onCategoriasMudaram}
            onConfirmar={onConfirmar}
            dica={dicaDoAbatimento(transaction, vinculos)}
          />
        ))}
      </ul>
    </div>
  )

  return (
    <>
      {futuros.length > 0 && (
        <div className="mb-2">
          <Dobra titulo={rotuloDasFuturas(futuros)}>{futuros.map(diaDosRecentes)}</Dobra>
        </div>
      )}
      {grupos.map(diaDosRecentes)}
    </>
  )
}

/**
 * A rosca, com o estado de "que fatia está aberta".
 *
 * **O drill NÃO vai para a URL**, ao contrário de tudo em Transações. Aqui ele
 * é um jeito de olhar mais de perto por três segundos, não uma visão que alguém
 * compartilha ou recarrega — e o Dashboard nem mês na URL tem. Um parâmetro a
 * mais custaria uma entrada no histórico do navegador por fatia tocada, e o
 * "voltar" deixaria de sair da tela.
 */
/** O gasto do mês anterior, ou `null` enquanto ele não chegou (ou falhou). */
function gastoAnterior(
  anterior: PreviousMonth,
  categories: Category[],
  qual: 'competencia' | 'caixa',
): number | null {
  if (anterior.status !== 'loaded') return null
  const entries = spendEntriesOf(
    qual === 'competencia' ? anterior.competence : anterior.cash,
    categories,
  )
  return qual === 'competencia' ? totalOutflow(entries) : totalCashOutflow(entries)
}

export function Dashboard() {
  const user = useReadyUser()
  const month: ReferenceMonth = currentReferenceMonth()
  const book = useMonthlyBook(month)
  const { transactionRepository } = useFinanceRepositories()

  /**
   * Grava e **corrige o livro no lugar** — a mesma escolha da aba de
   * Transações: reler o mês inteiro para mudar uma categoria refaria cinco
   * leituras e faria a tela piscar por um campo.
   */
  async function categorizar(id: string, categoryId: string | null) {
    if (book.status !== 'loaded') return
    // **A escolha à mão apaga a procedência automática** (Fase 10): quem
    // decidiu foi gente, e a bolinha azul — que quer dizer "confira isto" — não
    // faz sentido sobre uma categoria que a própria pessoa acabou de escolher.
    const categorySource = { kind: 'manual' as const, ruleId: null, confirmedAt: null }
    await transactionRepository.updateEnrichment(id, { categoryId, categorySource })
    book.patchEnrichment(id, { categoryId, categorySource })
  }

  /** Confere o que a tabela de regras aplicou, sem trocar a categoria. */
  async function conferir(id: string) {
    if (book.status !== 'loaded') return
    await transactionRepository.confirmAutoCategory([id])
    const atual = [...book.competence, ...book.cash].find((t) => t.id === id)
    const fonte = atual?.enrichment.categorySource
    if (fonte != null) {
      book.patchEnrichment(id, {
        // `'agora'` como marca otimista, igual ao `readAt` dos avisos: a data
        // de verdade é a do servidor, e chega na próxima leitura. O que a tela
        // precisa saber é só que deixou de estar por conferir.
        categorySource: { ...fonte, confirmedAt: 'agora' },
      })
    }
  }
  const anterior = useMonthTransactions(addMonths(month, -1))

  return (
    <section>
      <header className="mb-4 flex items-center justify-between gap-3">
        <div>
          {/*
            A saudação era o `h1` — o maior texto da tela, dizendo o que a
            pessoa já sabe. O tamanho agora vai para os números do mês, que é o
            que ela abriu o app para ver. O `h1` continua sendo a saudação
            porque é o título da tela; o que mudou foi o peso.
          */}
          <h1 className="titulo text-base">{`Olá, ${primeiroNome(user.displayName)} 👋`}</h1>
          <p className="text-xs text-[var(--text-muted)]">Resumo da casa</p>
        </div>
        <div className="flex items-center gap-2">
          {/* **Só o sino sobrou aqui** (decisão 252).

              Notas e Relatórios eram ícones nesta linha e viraram cartões
              abaixo do saldo. O sino fica porque ele não é um destino como os
              outros dois: ele **avisa**, e um aviso precisa estar onde o olho
              cai ao abrir o app, não numa fileira de atalhos no meio da
              rolagem. */}
          <Sino />
          {/* O único caminho de entrada para Ajustes, conforme a tabela de
              Docs/04-Design/Navegação.md. O avatar mostra a sessão — foto quando
              existe, iniciais quando não, ícone neutro quando nem isso. */}
          <Link
            to="/ajustes"
            aria-label="Ajustes"
            className="rounded-full outline-offset-4 focus-visible:outline-2"
          >
            <Avatar displayName={user.displayName} photoURL={user.photoURL} size={40} />
          </Link>
        </div>
      </header>

      {book.status === 'loading' && (
        <Glass level="frost" className="p-5">
          <p role="status" className="text-sm text-[var(--text-muted)]">
            Carregando {monthLabel(month)}…
          </p>
        </Glass>
      )}

      {book.status === 'failed' && (
        <Glass level="frost" className="p-5">
          <p role="alert" className="text-sm text-[var(--text-muted)]">
            Não foi possível carregar o resumo agora.
          </p>
        </Glass>
      )}

      {book.status === 'loaded' && (
        <>
          <Saldo accounts={book.accounts} />

          {/* **Notas e Relatórios como cartões, e não como ícones no topo.**

              Como ícone no cabeçalho eles eram dois glifos sem rótulo ao lado
              da saudação — alcançáveis, e mudos sobre o que fazem. Aqui cada um
              tem nome e uma linha dizendo para que serve, que é o que separa um
              atalho de um enfeite.

              Logo abaixo do saldo porque é a ordem da pergunta: quanto eu
              tenho, e o que eu faço agora. */}
          <div className="mb-4 grid grid-cols-2 gap-3">
            <CartaoDeNotas />
            <Cartao para="/relatorios" nome="Relatórios" dica="Compare os meses">
              <IconeRelatorio />
            </Cartao>
          </div>

          <Glass level="frost" className="mb-4 p-5">
            {/* O mês uma vez só. Repetido nos dois rótulos, ele os quebrava em
                duas linhas em 375px e colidia com os números. */}
            <p className="mb-2 text-xs text-[var(--text-muted)]">{monthLabel(month)}</p>
            <div className="flex gap-4">
              <Numero
                id="dash-competencia"
                title="Gastos (competência)"
                outflow={totalOutflow(spendEntriesOf(book.competence, book.categories))}
                net={totalSpend(spendEntriesOf(book.competence, book.categories))}
                previousOutflow={gastoAnterior(anterior, book.categories, 'competencia')}
              />
              <Numero
                id="dash-caixa"
                title="Saídas (caixa)"
                outflow={totalCashOutflow(spendEntriesOf(book.cash, book.categories))}
                net={totalCash(spendEntriesOf(book.cash, book.categories))}
                previousOutflow={gastoAnterior(anterior, book.categories, 'caixa')}
              />
            </div>
          </Glass>

          {/* A rosca virou `GastosPorCategoria` e mora ao lado da de Transações:
              a mesma pergunta em duas telas, uma implementação só (decisão
              293). Aqui ela vem com o atalho do "sem categoria", que é o
              chamado à ação DESTA tela. */}
          <Glass level="frost" className="mb-4 p-5">
            <GastosPorCategoria
              transactions={book.competence}
              categories={book.categories}
              comAtalho
            />
          </Glass>

          <Glass level="frost" className="p-5">
            <div className="mb-2 flex items-baseline justify-between gap-3">
              <h2 className="text-xs text-[var(--text-muted)]">Últimas transações</h2>
              <Link to="/transacoes" className="text-sm underline">
                Ver todas
              </Link>
            </div>
            <Ultimas
              competence={book.competence}
              cash={book.cash}
              categories={book.categories}
              creditCards={book.creditCards}
              accounts={book.accounts}
              today={todayInHousehold()}
              onCategorize={categorizar}
              onCategoriasMudaram={book.reload}
              onConfirmar={conferir}
            />
          </Glass>
        </>
      )}
    </section>
  )
}

/**
 * "Murilo Erhardt" → "Murilo". O cabeçalho é uma saudação, e ninguém é saudado
 * pelo nome inteiro. Sem nome, um cumprimento sem nome — melhor que "Olá, 👋".
 */
function primeiroNome(displayName: string | null): string {
  const nome = displayName?.trim().split(/\s+/)[0]
  return nome === undefined || nome === '' ? 'de novo' : nome
}
